// demoData.js — Generates fake market data for demo mode
// Returns { options, ticks, margins, results, accountMargins }

import { formatDate } from './calculations';

const DEMO_STOCKS = [
  { symbol: 'RELIANCE', underlying: 'Reliance Industries Limited', spot: 1285.6, sector: 'Conglomerate', category: 'A', publicPrivate: 'Private', resultDate: '20 Jan 2026', isFuture: false },
  { symbol: 'TCS', underlying: 'Tata Consultancy Services Limited', spot: 4023.5, sector: 'IT', category: 'A', publicPrivate: 'Private', resultDate: '13 Mar 2026', isFuture: true },
  { symbol: 'HDFCBANK', underlying: 'HDFC Bank Limited', spot: 1732.4, sector: 'Bank', category: 'A', publicPrivate: 'Private', resultDate: '18 Jan 2026', isFuture: false },
  { symbol: 'INFY', underlying: 'Infosys Limited', spot: 1856.3, sector: 'IT', category: 'A', publicPrivate: 'Private', resultDate: '10 Apr 2026', isFuture: true },
  { symbol: 'ICICIBANK', underlying: 'ICICI Bank Limited', spot: 1298.7, sector: 'Bank', category: 'A', publicPrivate: 'Private', resultDate: '22 Jan 2026', isFuture: false },
  { symbol: 'SBIN', underlying: 'State Bank of India', spot: 812.5, sector: 'Bank', category: 'A', publicPrivate: 'Public', resultDate: '5 Feb 2026', isFuture: false },
  { symbol: 'ITC', underlying: 'ITC Limited', spot: 465.8, sector: 'FMCG', category: 'A', publicPrivate: 'Private', resultDate: '28 Apr 2026', isFuture: true },
  { symbol: 'LT', underlying: 'Larsen & Toubro Limited', spot: 3456.1, sector: 'Infrastructure', category: 'A', publicPrivate: 'Private', resultDate: '23 Jan 2026', isFuture: false },
  { symbol: 'TATAMOTORS', underlying: 'Tata Motors Limited', spot: 745.2, sector: 'Auto', category: 'B', publicPrivate: 'Private', resultDate: '12 Feb 2026', isFuture: false },
  { symbol: 'MARUTI', underlying: 'Maruti Suzuki India Limited', spot: 12450.6, sector: 'Auto', category: 'A', publicPrivate: 'Private', resultDate: '28 Apr 2026', isFuture: true },
  { symbol: 'SUNPHARMA', underlying: 'Sun Pharmaceutical Industries Ltd', spot: 1823.4, sector: 'Pharma', category: 'B', publicPrivate: 'Private', resultDate: '8 Feb 2026', isFuture: false },
  { symbol: 'NIFTY', underlying: 'NIFTY 50', spot: 22856.5, sector: 'Index', category: 'A', publicPrivate: 'Index', resultDate: null, isFuture: false, isIndex: true },
  { symbol: 'BANKNIFTY', underlying: 'BANK NIFTY', spot: 48234.1, sector: 'Index', category: 'A', publicPrivate: 'Index', resultDate: null, isFuture: false, isIndex: true },
];

const DEMO_EXPIRIES = ['2026-02-27', '2026-03-27', '2026-04-24'];

// ═══════════════════════════════════════════════════════════
// DEMO INSTRUMENTS — for Tradable Universe demo mode
// ═══════════════════════════════════════════════════════════

export function generateDemoInstruments() {
  return DEMO_STOCKS.map((s, i) => ({
    id: i + 1,
    symbol: s.symbol,
    underlying: s.underlying,
    market_cap: s.isIndex ? 'N/A' : Math.round(s.spot * (Math.random() * 500 + 100)),
    public_private: s.publicPrivate,
    sector: s.sector,
    category: s.category,
    enabled: i < 8 ? 1 : 0  // First 8 enabled, rest disabled
  }));
}

// ═══════════════════════════════════════════════════════════
// DEMO MARKET DATA — for Market Watch demo mode
// ═══════════════════════════════════════════════════════════

export function generateDemoData() {
  const options = [];
  const ticks = {};
  const margins = {};
  const results = {};
  let tokenId = 100001;

  DEMO_STOCKS.forEach(s => {
    if (s.resultDate) {
      results[s.symbol] = { displayDate: s.resultDate, isFutureResult: s.isFuture, nextResultDate: s.isFuture ? s.resultDate : null };
    }
    DEMO_EXPIRIES.forEach(exp => {
      ['CE', 'PE'].forEach(type => {
        const isIdx = s.isIndex || false;
        const stepSize = isIdx ? (s.symbol === 'NIFTY' ? 50 : 100) : (s.spot > 5000 ? 100 : s.spot > 1000 ? 50 : 10);
        const otmOffset = type === 'CE' ? stepSize * 3 : -stepSize * 3;
        const strike = Math.round((s.spot + otmOffset) / stepSize) * stepSize;
        const lotSize = isIdx ? (s.symbol === 'NIFTY' ? 75 : 30) : Math.max(10, Math.ceil(200000 / s.spot / 10) * 10);
        const ltp = +(Math.random() * 60 + 5).toFixed(2);
        const margin = Math.round(lotSize * s.spot * (0.12 + Math.random() * 0.06));
        const change = +(Math.random() * 8 - 4).toFixed(2);
        const close = +(ltp - change).toFixed(2);
        const token = tokenId++;
        const tradingsymbol = `${s.symbol}${exp.replace(/-/g, '').slice(2)}${strike}${type}`;
        const bestBid = +(ltp - 0.5 - Math.random() * 2).toFixed(2);
        const bestAsk = +(ltp + 0.5 + Math.random() * 2).toFixed(2);

        options.push({
          token, symbol: s.symbol, underlying: s.underlying, strike, type, expiry: exp,
          expiryStr: formatDate(exp), lotSize, tradingsymbol,
          sector: s.sector, category: s.category, publicPrivate: s.publicPrivate, isIndex: isIdx,
          spot: s.spot, underlyingToken: null
        });
        ticks[token] = {
          ltp, change, close, volume: Math.round(Math.random() * 500000),
          oi: Math.round(Math.random() * 2000000), source: 'demo',
          iv: +(Math.random() * 20 + 12).toFixed(1),
          depth: {
            buy: [{ price: bestBid, quantity: Math.round(Math.random() * 5000) + 100 }],
            sell: [{ price: bestAsk, quantity: Math.round(Math.random() * 5000) + 100 }]
          }
        };
        margins[tradingsymbol] = { totalMargin: margin };
      });
    });
  });

  const accountMargins = {
    'KITE_1': { label: 'Shubh Kite', available: 520000, used: 180000 },
    'KOTAK_1': { label: 'Shubh Kotak', available: 380000, used: 95000 }
  };

  return { options, ticks, margins, results, accountMargins };
}
